import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import core, { API, GQL } from '@gin-betting/core';
import { noop } from 'lodash';

type OutrightsCountBySport = Record<string, number>;

interface Output {
    outrightsCountBySport: OutrightsCountBySport;
    categorizerItems: API.CategorizerItem[];
}

const {
    utils: { getBettingOptions },
    poller: { getPollerManager },
    appServiceLocator,
} = core;

const API_CLIENT = appServiceLocator.get('apiClient');

const pollInterval$ = getPollerManager().getIntervalObserve();

const useOutrightsCountBySport = (): Output => {
    const [
        { outrightsCountBySport, categorizerItems },
        setOutrightsCountBySport,
    ] = useState<{
        outrightsCountBySport: OutrightsCountBySport;
        categorizerItems: API.CategorizerItem[];
    }>({ outrightsCountBySport: {}, categorizerItems: [] });

    const { pathname } = useLocation();

    const getOutrightsCategorizer = useCallback(
        () =>
            API_CLIENT.makeRequest({
                method: 'getCategorizer',
                variables: {
                    matchStatuses: [
                        GQL.SportEventStatus.NOT_STARTED,
                        GQL.SportEventStatus.SUSPENDED,
                        GQL.SportEventStatus.LIVE,
                    ],
                    marketStatuses: [
                        GQL.MarketStatus.ACTIVE,
                        GQL.MarketStatus.SUSPENDED,
                    ],
                    sportIds: getBettingOptions().sportIds,
                    sportEventTypes: [GQL.SportEventType.OUTRIGHT],
                },
            })
                .then(({ data }) =>
                    setOutrightsCountBySport({
                        outrightsCountBySport: reduceOutrightsCount(data),
                        categorizerItems: data.categorizer as API.CategorizerItem[],
                    })
                )
                .catch(noop),
        []
    );

    useEffect(() => {
        getOutrightsCategorizer();

        const sub$ = pollInterval$.subscribe(() => {
            getOutrightsCategorizer();
        });

        return () => {
            sub$.unsubscribe();
        };
    }, [pathname, getOutrightsCategorizer]);

    return useMemo(() => ({ outrightsCountBySport, categorizerItems }), [
        outrightsCountBySport,
        categorizerItems,
    ]);
};

function reduceOutrightsCount(
    data: GQL.GetCategorizerQuery
): OutrightsCountBySport {
    if (!data.categorizer) return {};

    return data.categorizer.reduce((record: OutrightsCountBySport, catItem) => {
        record[catItem.value] = catItem.count;

        return record;
    }, {});
}

export default useOutrightsCountBySport;
